import { TrashIcon } from "lucide-react";
import type { Control, UseFormReturn } from "react-hook-form";

import { SelectorDeComuna } from "@/components/informes/SelectorDeComuna";
import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";

import type { InformeValues } from "./InformeFormulario";

/**
 * El rol vacío con el que nace una fila nueva de la tabla.
 *
 * SIN `id`, y esa ausencia es el dato: significa "créalo". El servidor
 * reconcilia por `id` desde que el rol es dueño de la imagen de su límite y de
 * la descripción de sus deslindes, así que una fila nueva no tiene nada que
 * nombrar todavía.
 */
export const ROL_VACIO = {
  id: undefined as number | undefined,
  rol_sii: "",
  comuna_codigo: "",
  comuna: "",
  superficie: "",
  superficie_construida: "",
};

/** Un rol recién nacido, sin compartir referencia con otro. */
export function rolVacio() {
  return { ...ROL_VACIO };
}

/**
 * Una fila de la tabla "Detalle inmuebles tasación": un rol con su comuna y
 * sus dos superficies.
 *
 * REVERSIÓN DELIBERADA del nivel "propiedad" que agrupaba varios roles bajo un
 * mismo inmueble. Esa agrupación nació de la cotización 22160, que en
 * realidad eran CINCO tasaciones distintas metidas en un solo documento —el
 * caso incorrecto—; la 22192 (una propiedad, varios roles, una comuna) es el
 * caso correcto, y agruparlos no le agregaba nada que el cuadro de honorarios
 * necesitara: ahora el informe entero es UNA tasación, así que cuántos roles
 * "van juntos" dejó de ser una decisión comercial que la pantalla tuviera que
 * capturar. Cada rol es su propia fila, con su propia comuna: el caso real de
 * una sucesión con roles en comunas distintas queda representado sin forzar
 * nada.
 */
export function FilaDeRol({
  control,
  form,
  indice,
  total,
  unidad,
  onQuitar,
}: {
  control: Control<InformeValues>;
  form: UseFormReturn<InformeValues>;
  indice: number;
  total: number;
  unidad: "ha" | "m2";
  onQuitar: () => void;
}) {
  const base = `roles.${indice}` as const;

  return (
    <TableRow>
      <TableCell className="align-top">
        <FormField
          control={control}
          name={`${base}.rol_sii`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input aria-label="Rol SII" placeholder="34-8" autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="align-top">
        {/* La comuna se ELIGE del registro oficial, no se escribe. Escrita a
            mano entraba de cinco formas distintas para la misma comuna, y rol
            + comuna es la clave con la que el SII reconoce la parcela: una
            clave tipeada no cruza contra nada. */}
        <FormField
          control={control}
          name={`${base}.comuna_codigo`}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormControl>
                <SelectorDeComuna
                  aria-label="Comuna"
                  valor={field.value || null}
                  onValorChange={(codigo) => field.onChange(codigo ?? "")}
                  // El nombre congelado del rol viejo: sin él, uno anterior al
                  // maestro mostraría el placeholder y parecería que perdió
                  // el dato.
                  nombreCongelado={form.getValues(`${base}.comuna`)}
                  invalido={fieldState.invalid}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="align-top">
        <FormField
          control={control}
          name={`${base}.superficie`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  aria-label={`Superficie (${unidad})`}
                  placeholder="Vacío = no aplica"
                  inputMode="decimal"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="align-top">
        <FormField
          control={control}
          name={`${base}.superficie_construida`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                {/* El placeholder dice qué se imprime si queda vacío, y no es
                    lo mismo que en terreno: acá "s/i" significa que la
                    construcción existe y nadie la midió; allá el guion
                    significa que no corresponde. */}
                <Input
                  aria-label="Superficie construida (m2)"
                  placeholder="Vacío = s/i"
                  inputMode="decimal"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="align-top">
        {/* Quitar aparece solo con más de un rol: con uno solo, borrarlo
            dejaría el informe sin ningún rol, que el esquema prohíbe. */}
        {total > 1 ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="border-border text-ink-ghost hover:border-danger-dot/40 hover:bg-danger-soft hover:text-danger-soft-foreground dark:hover:bg-danger-soft size-8 rounded-md border-[1.5px]"
            onClick={onQuitar}
            aria-label={`Eliminar el rol ${indice + 1}`}
          >
            <TrashIcon className="size-3.5" />
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
